/**
 * OnboardingTour — Auto-Trigger der First-Run-Orientierungs-Tour.
 *
 * In `App.tsx` (authed-Branch) gemountet, bekommt den eingeloggten User als
 * Prop. Startet die Tour genau einmal pro Session wenn:
 *   - `me.onboardedAt == null` (Tour noch nicht gesehen),
 *   - `!me.mustChangePassword` (Forced-Change-Screen geht vor — der greift in
 *     App.tsx ohnehin VOR diesem Mount; doppelt gesichert),
 *   - die Shell-Anker im DOM sind (rAF + Retry; Sidebar/Topbar mounten kurz nach).
 *
 * Tour-Ende (Finish/Skip/Esc/Overlay) → `POST /api/auth/onboarding/complete`.
 * KEIN optimistic me-Update (Auth-State ist privat in App.tsx, kein Setter-
 * Vertrag) — der Session-Ref-Guard verhindert Re-Trigger same-session; cross-
 * session greift das Backend-Flag via nächstem `/me`-Boot. Mark-Fehler non-fatal.
 */
import { useEffect, useRef } from 'react';
import { api } from '../lib/api';
import { startTour, type DriverFactory } from '../lib/onboarding-tour';
import type { AuthedUser } from '../lib/auth-types';

interface Props {
  me: AuthedUser | null;
  /** Test-Seam: injizierbare driver.js-Factory. */
  factory?: DriverFactory;
  /** Test-Seam: rAF/Delay überspringen (synchroner Start). */
  immediate?: boolean;
}

const MAX_ANCHOR_RETRIES = 10;
const RETRY_MS = 200;
// Desktop-first: auf Mobile liegt die Sidebar im geschlossenen Drawer off-screen
// (im DOM, aber translate-x-full) → der Anker-Filter würde sie finden und
// driver.js ins Leere spotlighten. Auto-Trigger daher nur ab sm-Breakpoint.
const MIN_VIEWPORT_WIDTH = 640;

export function OnboardingTour({ me, factory, immediate = false }: Props): null {
  // Genau 1× pro Session — überlebt Re-Renders, Navigation, /me-Refetch.
  const triggeredRef = useRef(false);

  useEffect(() => {
    if (triggeredRef.current) return;
    if (!me || me.onboardedAt != null || me.mustChangePassword) return;
    if (window.innerWidth < MIN_VIEWPORT_WIDTH) return; // Desktop-first (s.o.)

    let cancelled = false;
    let attempts = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let raf: number | undefined;

    const tryStart = (): void => {
      if (cancelled || triggeredRef.current) return;
      const started = startTour({
        factory,
        onEnd: () => {
          // fire-and-forget: Mark-Fehler darf die UI nicht brechen.
          void api('/api/auth/onboarding/complete', { method: 'POST' }).catch((err) => {
            console.error('[onboarding] mark complete failed:', err);
          });
        },
      });
      if (started) {
        triggeredRef.current = true;
      } else if (++attempts < MAX_ANCHOR_RETRIES) {
        // Shell-Anker noch nicht im DOM → später erneut versuchen.
        timer = setTimeout(tryStart, RETRY_MS);
      }
    };

    if (immediate) {
      tryStart();
    } else {
      // rAF + kleiner Delay: Layout-Shell (Sidebar/Topbar) muss gemountet sein.
      raf = requestAnimationFrame(() => {
        timer = setTimeout(tryStart, 100);
      });
    }

    return () => {
      cancelled = true;
      if (raf) cancelAnimationFrame(raf);
      if (timer) clearTimeout(timer);
    };
  }, [me, factory, immediate]);

  return null;
}
